import ErrorBoundary from 'components/ErrorBoundary';
import ErrorFallback from 'components/feedback/ErrorFallback';
import Clock from 'features/clock/Clock';
import Map from 'features/map/Map';
import PrioritizedVehicleList from 'features/prioritized-deviations/PrioritizedVehicleList';
import { parseIntoPVLItems, prioritizeDeviations } from 'features/prioritized-deviations/utils';
import VehicleSearch from 'features/vehicle-search/VehicleSearch';
import { getVehicleByVin } from 'helper/vehicleV2';
import { useClock } from 'hooks/useClock';
import useGetMissionData from 'hooks/useGetMissionData';
import useMultimonitorSetup from 'hooks/useMultimonitorSetup';
import { useShallowStable } from 'hooks/useShallowStable';
import { useThrottledValue } from 'hooks/useThrottledValue';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useListHubsQuery } from 'services/hubs';
import { normalizeRecordData } from 'services/utils';
import { useListVehiclesV2LiveQuery, useListVehiclesV2Query } from 'services/vehiclesV2';
import { getSelectedVehicle, setIsVehicleDetailsOpen } from 'store/slices/appSlice';
import { getSystemStatus } from 'store/slices/healthStatusSlice';

import styles from './styles/MainPage.module.scss';

// import useCenterVehicle from 'hooks/useCenterVehicle'

interface MainPageProps {
    userId: string;
}

const MainPage = ({ userId }: MainPageProps) => {
    const dispatch = useDispatch();
    const isSystemOk = useSelector(getSystemStatus);
    const { data: hubsData, error: listHubsError } = useListHubsQuery(`ListHubs-${userId}`, {
        refetchOnMountOrArgChange: true,
    });
    const selectedVehicleState = useSelector(getSelectedVehicle);
    const selectedVIN = selectedVehicleState?.selectedVIN;

    useEffect(() => {
        // enforce opening the VehicleDetails page on page load despite any persisted setting
        dispatch(setIsVehicleDetailsOpen(false));
    }, [dispatch]);

    const {
        data: vehiclesData,
        error: onUpdatedVehicleError,
        prioritizedDeviations,
        isLoading,
    } = useListVehiclesV2Query(
        {},
        {
            refetchOnMountOrArgChange: true,
            selectFromResult: state => {
                const prioritizedList = prioritizeDeviations(state.data);
                return {
                    ...state,
                    prioritizedDeviations: prioritizedList,
                };
            },
        }
    );

    // Keeps the live map in sync: this carrier owns the onUpdateVehicleV2 subscription and
    // cross-patches live telemetry into the REST listVehiclesV2 cache above.
    useListVehiclesV2LiveQuery({});

    const { data: missionData } = useGetMissionData({ vin: selectedVIN });

    useMultimonitorSetup(missionData?.id);

    const datetime = useClock();

    const normalizedVehicles = useMemo(() => normalizeRecordData(vehiclesData), [vehiclesData]);

    const spreadDeviations = useMemo(() => {
        if (!prioritizedDeviations) {
            return [];
        }
        const { fatals = [], errors = [], warnings = [] } = prioritizedDeviations;
        return [...fatals, ...errors, ...warnings];
    }, [prioritizedDeviations]);

    const throttledVehicles = useThrottledValue(normalizedVehicles, 200, {
        leading: true,
        trailing: true,
    });
    const throttledDeviations = useThrottledValue(spreadDeviations, 2000, {
        leading: true,
        trailing: true,
    });
    const throttledHubs = useThrottledValue(hubsData, 2000, {
        leading: true,
        trailing: true,
    });

    const stableVehicles = useShallowStable(throttledVehicles);
    const stableDeviations = useShallowStable(throttledDeviations);
    const stableHubs = useShallowStable(throttledHubs);

    const selectedVehicle = useMemo(
        () => (selectedVIN ? getVehicleByVin(vehiclesData, selectedVIN) : undefined),
        [selectedVIN, vehiclesData]
    );

    return (
        <main className={`${styles.MainPage} display-flex padding-0 overflow-hidden`} data-testid='main-page-content'>
            <div
                data-testid='main-page-map'
                className={`${styles.MainPage__map} ${!isSystemOk && styles['MainPage__map--narrowed']} position-relative`}
            >
                <div
                    data-testid='main-page-header'
                    className={`${styles.MainPage__header} width-100pct padding-x-15 padding-y-0 display-flex justify-content-between flex-wrap gap-5 position-absolute top-15`}
                >
                    <Clock datetime={datetime} />
                    <VehicleSearch vehicles={normalizedVehicles} />
                </div>
                <ErrorBoundary
                    FallbackComponent={ErrorFallback}
                    error={listHubsError}
                    containerClassName='mt-70px mx-10'
                >
                    <Map
                        activeMission={missionData}
                        vehicles={stableVehicles}
                        hubs={stableHubs}
                        deviations={stableDeviations}
                        selectedVehicle={selectedVehicle}
                    />
                </ErrorBoundary>
            </div>
            <section data-testid='main-page-pvl' className={`${styles.MainPage__pvl} overflow-x-hidden bg-white`}>
                <ErrorBoundary FallbackComponent={ErrorFallback} error={onUpdatedVehicleError}>
                    <PrioritizedVehicleList
                        inMRM={parseIntoPVLItems(normalizedVehicles)?.inMRM}
                        others={parseIntoPVLItems(normalizedVehicles)?.others}
                        isLoading={isLoading}
                    />
                </ErrorBoundary>
            </section>
        </main>
    );
};
export default MainPage;
